import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, MoreVertical } from 'lucide-react'
import {
  Button,
  DataTable,
  DateField,
  Label,
  MultiSelect,
  SearchInput,
  type DataTableColumn,
  type DataTableProps,
} from '@atrvd/saas-ui'
import { useCreditCardAvailability, useFacilities, usePacks } from '@/hooks/useBanking'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import {
  formatAccountNumber,
  NO_PACK,
  UNASSIGNED_FACILITY,
  packFilterOptions,
  pruneFacilitySelection,
  toFilterEntries,
} from '@/components/ledgersync/connectionManagement.utils'
import type { CreditCardAvailabilityRow, CreditCardSortColumn } from '@/lib/db-tenant'
import { DataWarningIcon } from '@/components/banking/DataWarningIcon'
import { creditCardConnectionWarning } from './creditCardConnectionWarning'
import { formatCurrency, formatDate, formatDateTime } from '@dcp/shared/utils/formatters'

/**
 * A synthetic row that carries one end of the infinite-scroll window.
 *
 * A row rather than a sibling element because `DataTable` owns its own
 * scroll container: an observer target placed outside it would never intersect.
 * Same technique as the connection accounts table.
 *
 * There are TWO of them because the window slides in both directions — `end`
 * loads the next step, `start` brings back the page that was dropped off the
 * top — and the pair only ever appears together on a window far larger than a
 * viewport, so neither can trigger the other.
 */
interface SentinelRow {
  __kind: 'sentinel'
  edge: 'start' | 'end'
  id: string
}

/** Either a real card or one of the synthetic sentinels. */
type TableRow = CreditCardAvailabilityRow | SentinelRow

/** Narrow a table row to a synthetic sentinel. */
function isSentinelRow(row: TableRow): row is SentinelRow {
  return '__kind' in row && row.__kind === 'sentinel'
}

/**
 * The facilities a given Pack selection admits.
 *
 * ONE definition, called from two places that must never disagree: the Facility
 * dropdown's options and the pruning that runs when the Pack selection changes.
 * Held together here because a rule that lives in two copies drifts — add a
 * second sentinel pack and fix only one copy, and the dropdown offers one set
 * while the applied filter uses another, which shows up as an empty table with
 * every control saying "All" (raised in review of PR #238).
 *
 * An empty selection means "no pack filter", i.e. every active facility. The
 * NO_PACK sentinel selects the facilities that belong to no pack.
 *
 * @param activeFacilities - Already scoped to `is_active`
 * @param packSelection - Pack ids, possibly including the NO_PACK sentinel
 * @returns The subset the selection admits, in the order given
 */
function facilitiesForPackSelection<T extends { pack_id: string | null }>(
  activeFacilities: T[],
  packSelection: string[],
): T[] {
  if (packSelection.length === 0) return activeFacilities
  const packIds = new Set(packSelection.filter((value) => value !== NO_PACK))
  const wantsPackless = packSelection.includes(NO_PACK)
  return activeFacilities.filter((f) =>
    f.pack_id == null ? wantsPackless : packIds.has(f.pack_id),
  )
}

/**
 * Keystroke-to-query delay for the search box.
 *
 * The term is part of the RPC's query key, so an undebounced box issues a
 * server round trip PER CHARACTER — typing "platinum" measured six requests —
 * and each one restarts the scroll window from page one while the user is
 * still mid-word. `SearchInput` does carry its own debounce, but it is on
 * `onSearch`; `onChange` fires immediately, which is what the input's value
 * has to be bound to. So the delay belongs here, between the two.
 *
 * 300ms is what the accounts table and the bank-catalog search already use.
 */
const SEARCH_DEBOUNCE_MS = 300

/**
 * The sort state `DataTable` actually accepts.
 *
 * Derived from its props rather than imported: saas-ui has TWO `SortState`
 * shapes — the hooks one (keyed `key`) and the table one (keyed `columnKey`) —
 * and the name it exports is the FORMER, while `DataTable.sorting` wants the
 * latter. Importing the exported name compiles against the wrong shape and
 * fails only where the two meet.
 */
type TableSortState = NonNullable<
  NonNullable<DataTableProps<TableRow>['sorting']>['sortState']
>[number]

/**
 * Credit Card Availability (DCP-374).
 *
 * Lists the organization's credit cards with the terms their banks publish —
 * BOTH the API-connected ones and the manual ones, per AC#1. Which cards
 * appear is the RPC's decision, not this component's: a card must not be
 * disconnected or provider-removed, must be assigned to a facility, and that
 * facility must be active.
 *
 * AVAILABILITY IS COMPUTED BY THE RPC, not taken from the feed. AC#8.2 defines
 * it as limit minus usage, which in our signs (what is owed is negative) is
 * `credit_limit + balance`. Empty whenever either operand is missing — a
 * partial sum would report a card as fully drawn on no evidence — while a
 * genuine zero stays zero and shows as $0.00, because "no credit left" is a
 * fact an AP user acts on. The vendor's own `available_credit` is returned
 * beside it and deliberately NOT rendered.
 *
 * Balances keep the vendor's sign, where what is OWED is negative.
 *
 * NOTHING IS FILTERED OR SORTED HERE either. The table grows by scrolling, so
 * the component holds only what it has fetched; the filters, the search term
 * and the ordering all travel to the RPC.
 */
export function CreditCardAvailabilityPage() {
  const { data: facilities = [] } = useFacilities()
  const { data: packs = [] } = usePacks()

  /**
   * Two pieces of state for one box: `search` is what the input shows and
   * updates on every keystroke, `searchTerm` is what the query uses and
   * settles {@link SEARCH_DEBOUNCE_MS} later. A single value cannot do both —
   * binding the input to the debounced one makes typing lag visibly.
   */
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>()
  const [packFilter, setPackFilter] = useState<string[]>([])
  const [facilityFilter, setFacilityFilter] = useState<string[]>([])
  const [dueFrom, setDueFrom] = useState('')
  const [dueTo, setDueTo] = useState('')
  /** AC#3: Facility ascending, then card name — the RPC's own default. */
  const [sortState, setSortState] = useState<TableSortState[]>([
    { columnKey: 'facility', direction: 'asc' },
  ])

  /** Only ACTIVE facilities can hold a visible card, so only they are offered. */
  const activeFacilities = useMemo(() => facilities.filter((f) => f.is_active), [facilities])

  /**
   * AC#7.1: once packs are picked, the Facility filter offers only their
   * facilities.
   *
   * NOT the shared `facilityFilterOptions`. Both narrow the facilities the same
   * way — a pack's own facilities, and the pack-less ones under "No Pack" — but
   * the shared helper also appends the UNASSIGNED_FACILITY sentinel (always
   * with no pack picked, and under "No Pack"), because the connection-accounts
   * table has accounts without a facility. A card without one cannot reach this
   * page at all (the RPC inner-joins facilities), so here that option could
   * only ever return nothing and is never offered.
   */
  const facilityOptions = useMemo(
    () =>
      facilitiesForPackSelection(activeFacilities, packFilter).map((f) => ({
        value: f.id,
        label: f.name,
      })),
    [activeFacilities, packFilter],
  )

  /**
   * Narrowing the options is not enough on its own: a facility chosen BEFORE a
   * pack was picked would stay in the query while vanishing from the dropdown —
   * an invisible filter whose symptom is an empty table with every control
   * saying "All".
   */
  const handlePackChange = (next: string[]) => {
    setPackFilter(next)
    setFacilityFilter((current) => {
      // Pruned against the options THIS page offers — the same function the
      // dropdown uses, so the two cannot disagree.
      const allowed = facilitiesForPackSelection(activeFacilities, next)
      return pruneFacilitySelection(current, allowed.map((f) => ({ value: f.id })))
    })
  }

  const handleSearchChange = useCallback((next: string) => {
    setSearch(next)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    // Trimmed here rather than at the query, so the timer is not restarted by
    // a change that cannot alter the term (typing and deleting a space).
    const trimmed = next.trim()
    searchDebounce.current = setTimeout(() => setSearchTerm(trimmed), SEARCH_DEBOUNCE_MS)
  }, [])

  // A pending keystroke must not fire into an unmounted component.
  useEffect(() => () => clearTimeout(searchDebounce.current), [])

  const sort = (sortState[0]?.columnKey ?? 'facility') as CreditCardSortColumn
  const direction: 'asc' | 'desc' = sortState[0]?.direction === 'desc' ? 'desc' : 'asc'

  const query = useMemo(
    () => ({
      search: searchTerm || undefined,
      packIds: toFilterEntries(packFilter, NO_PACK),
      facilityIds: toFilterEntries(facilityFilter, UNASSIGNED_FACILITY),
      dueFrom: dueFrom || undefined,
      dueTo: dueTo || undefined,
      sort,
      direction,
    }),
    [searchTerm, packFilter, facilityFilter, dueFrom, dueTo, sort, direction],
  )

  /**
   * An impossible window, typed rather than picked.
   *
   * `DateField`'s `min`/`max` only grey out days in the CALENDAR — saas-ui is
   * explicit that they are an affordance and that a typed out-of-range date
   * still reaches state. So "Due to before Due from" is reachable, and the RPC
   * answers it honestly with nothing, which reads on screen as "this
   * organization has no cards due" rather than "you asked for a window that
   * cannot contain a date". Detected here so the empty state can say which.
   */
  const dueRangeInverted = Boolean(dueFrom && dueTo && dueFrom > dueTo)

  const cards = useCreditCardAvailability(query)

  // `!cards.error` stops the sentinel from re-requesting a step that has just
  // failed: the observer fires again the moment the fetch settles, so without
  // it a failing endpoint is hammered for as long as the user sits there.
  const { sentinelRef: endRef, enabled: endEnabled } = useInfiniteScroll({
    hasMore: cards.hasMore && !cards.error,
    isFetching: cards.isFetching,
    onLoadMore: cards.loadMore,
  })

  /**
   * The same trigger at the top of the window. `rootMargin` is zeroed here:
   * pre-loading ahead of the leading edge would re-fetch the dropped page while
   * the user is still scrolling DOWN past it, undoing the drop.
   */
  const { sentinelRef: startRef, enabled: startEnabled } = useInfiniteScroll({
    hasMore: cards.hasPrevious && !cards.error,
    isFetching: cards.isFetching,
    onLoadMore: cards.loadPrevious,
    rootMargin: '0px',
  })

  /**
   * The retained cards, bracketed by a sentinel wherever the window has a step
   * left to load. Each is added only when that end IS loadable, so a table
   * holding the whole result set has no stray row at either end.
   */
  const rows = useMemo<TableRow[]>(() => {
    if (cards.rows.length === 0) return []
    const out: TableRow[] = []
    if (startEnabled) out.push({ __kind: 'sentinel', edge: 'start', id: '__sentinel_start__' })
    out.push(...cards.rows)
    if (endEnabled) out.push({ __kind: 'sentinel', edge: 'end', id: '__sentinel_end__' })
    return out
  }, [cards.rows, startEnabled, endEnabled])

  /**
   * Column widths are explicit so the nine money columns keep their headers on
   * ONE line and the table scrolls sideways instead of squeezing "Current
   * Balance (Usage)" into three stacked words. `whitespace-nowrap` on the
   * header is what makes the width a floor rather than a suggestion.
   */
  const columns: DataTableColumn<TableRow>[] = useMemo(
    () => [
      {
        key: 'facility',
        header: <span className="whitespace-nowrap">Facility</span>,
        headerLabel: 'Facility',
        sortable: true,
        minWidth: '140px',
        render: (row) => (isSentinelRow(row) ? null : row.facility_name),
      },
      {
        key: 'name',
        header: <span className="whitespace-nowrap">Credit Card</span>,
        headerLabel: 'Credit Card',
        sortable: true,
        minWidth: '220px',
        // AC#4.3: name plus the LAST FOUR, whatever the source. A card
        // connected through LedgerSync has only the vendor's `mask`, which its
        // API guarantees is "never more than 4 digits", so masking it is a
        // no-op; a MANUAL card (AC#1 puts those on this page too) carries
        // whatever the user typed in Banking Settings, and that is trimmed to
        // the same four here.
        //
        // `formatAccountNumber` rather than a local conditional, so this page
        // cannot drift from the screens the same account appears on —
        // ManualBankAccountsTable and ManualTransactionsTable both mask, and
        // one screen showing a full number that the others hide is worse than
        // either choice made consistently (PR #238 review).
        //
        // Still guarded on presence: the helper answers an em dash for an
        // empty value, which beside the name would read as a number rather
        // than as "there is none".
        //
        // A card on an unhealthy connection stays listed with its last known
        // figures; the warning icon beside the name says how old they are.
        render: (row) => {
          if (isSentinelRow(row)) return null
          const warning = creditCardConnectionWarning(row)
          return (
            <span className="inline-flex items-center gap-1">
              <span>
                <span className="font-medium">{row.name}</span>
                {row.account_number ? (
                  <span className="ml-1 text-muted-foreground">
                    {formatAccountNumber(row.account_number)}
                  </span>
                ) : null}
              </span>
              {warning && <DataWarningIcon message={warning} />}
            </span>
          )
        },
      },
      {
        key: 'credit_limit',
        header: <span className="whitespace-nowrap">Total Credit Limit</span>,
        headerLabel: 'Total Credit Limit',
        sortable: true,
        cellClassName: 'tabular-nums whitespace-nowrap',
        minWidth: '150px',
        render: (row) => (isSentinelRow(row) ? null : money(row.credit_limit)),
      },
      {
        key: 'balance',
        header: <span className="whitespace-nowrap">Current Balance (Usage)</span>,
        headerLabel: 'Current Balance (Usage)',
        sortable: true,
        cellClassName: 'tabular-nums whitespace-nowrap',
        minWidth: '190px',
        // Signed as the vendor signs it: what is owed is negative.
        render: (row) => (isSentinelRow(row) ? null : money(row.balance)),
      },
      {
        key: 'available_credit',
        header: <span className="whitespace-nowrap">Availability</span>,
        headerLabel: 'Availability',
        sortable: true,
        cellClassName: 'tabular-nums whitespace-nowrap',
        minWidth: '130px',
        // AC#8.2's own figure — limit minus usage, computed by the RPC — not
        // the vendor's `available_credit`. Empty whenever either operand is
        // missing; a real zero shows as $0.00, because a card with nothing
        // left is a fact and must not read as "unknown".
        render: (row) => (isSentinelRow(row) ? null : money(row.availability)),
      },
      {
        key: 'payment_due_date',
        header: <span className="whitespace-nowrap">Payment Due Date</span>,
        headerLabel: 'Payment Due Date',
        sortable: true,
        cellClassName: 'whitespace-nowrap',
        minWidth: '150px',
        render: (row) =>
          isSentinelRow(row) ? null : row.payment_due_date ? formatDate(row.payment_due_date) : '—',
      },
      {
        key: 'statement_balance',
        header: <span className="whitespace-nowrap">Statement Balance</span>,
        headerLabel: 'Statement Balance',
        sortable: true,
        cellClassName: 'tabular-nums whitespace-nowrap',
        minWidth: '155px',
        render: (row) => (isSentinelRow(row) ? null : money(row.statement_balance)),
      },
      {
        key: 'notes',
        header: <span className="whitespace-nowrap">Notes</span>,
        headerLabel: 'Notes',
        cellClassName: 'text-muted-foreground',
        minWidth: '120px',
        // No note behind this column yet: `bank_accounts` has no field for one
        // and manual entry is DCP-373. Rendered so the layout matches what was
        // agreed, and empty rather than absent so the gap stays visible.
        render: (row) => (isSentinelRow(row) ? null : '—'),
      },
      {
        key: 'last_updated',
        header: <span className="whitespace-nowrap">Last Updated</span>,
        headerLabel: 'Last Updated',
        sortable: true,
        cellClassName: 'whitespace-nowrap',
        minWidth: '170px',
        // When anything about this account last MOVED — balance or terms. A
        // trigger stamps it on change only, so a daily sync that reads the
        // same numbers leaves it where it was, which is what makes the column
        // worth reading.
        render: (row) =>
          isSentinelRow(row)
            ? null
            : row.data_updated_at
              ? formatDateTime(row.data_updated_at)
              : '—',
      },
      {
        key: 'actions',
        header: '',
        width: '56px',
        // Inert on purpose: Sync and Edit are not part of this release (BA
        // decision), but the affordance stays so the column does not move when
        // they arrive.
        render: (row) =>
          isSentinelRow(row) ? null : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled
              aria-label="Card actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          ),
      },
    ],
    [],
  )

  return (
    // A COLUMN THAT EXACTLY FILLS ITS PARENT, so the page never produces a
    // scrollbar of its own. The layout's <main> is `flex-1 overflow-y-auto`:
    // anything taller than it scrolls THERE, and with a table that also
    // scrolls the user gets two bars side by side and no way to tell which one
    // moves the rows. Fixing the column to `h-full` and letting the table take
    // the leftover with `flex-1 min-h-0` leaves exactly one.
    <div className="flex h-full flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">Credit Card Availability</h1>

      {/*
        Search anchored LEFT, the pickers pushed RIGHT by `ml-auto`, with the
        space between them doing the giving and taking. Stretching search to
        fill that space instead made one text box dominate a row of narrow
        dropdowns.

        No transition is declared here: the sidebar animates its own width
        (`transition-[width] duration-300`), the content column follows it frame
        by frame, and a row laid out this way reflows with it — the controls
        glide apart on collapse rather than jumping.
      */}
      <div className="flex flex-nowrap items-end gap-3">
        <div className="w-[320px] shrink-0 space-y-1.5">
          <Label htmlFor="cc-search">Search</Label>
          <SearchInput
            id="cc-search"
            placeholder="Search by card name or number..."
            value={search}
            onChange={handleSearchChange}
            clearable
          />
        </div>

        <div className="ml-auto flex flex-nowrap items-end gap-3">
          <div className="w-[190px] space-y-1.5">
            <Label>Pack</Label>
            <MultiSelect
              options={packFilterOptions(packs)}
              value={packFilter}
              onChange={handlePackChange}
              placeholder="All packs"
            />
          </div>
          <div className="w-[200px] space-y-1.5">
            <Label>Facility</Label>
            <MultiSelect
              options={facilityOptions}
              value={facilityFilter}
              onChange={setFacilityFilter}
              placeholder="All facilities"
            />
          </div>
          {/*
            Two independent fields rather than saas-ui's DateRangePicker: that
            component requires BOTH ends and works in `Date`, while AC#7.2
            allows either bound alone — and `DateField` is the repo's
            timezone-safe calendar input, built so a bare YYYY-MM-DD can never
            shift a day. The cross-bound min/max is AC#7.2's "end cannot precede
            start" — as a HINT, not a lock: it greys the impossible days out of
            each calendar, while a typed date still reaches state (saas-ui
            documents this), which is why `dueRangeInverted` above catches what
            slips past.
          */}
          <div className="w-[165px] space-y-1.5">
            <Label htmlFor="cc-due-from">Due from</Label>
            <DateField
              id="cc-due-from"
              value={dueFrom}
              onValueChange={setDueFrom}
              max={dueTo || undefined}
            />
          </div>
          <div className="w-[165px] space-y-1.5">
            <Label htmlFor="cc-due-to">Due to</Label>
            <DateField
              id="cc-due-to"
              value={dueTo}
              onValueChange={setDueTo}
              min={dueFrom || undefined}
            />
          </div>
        </div>
      </div>

      {/*
        A FAILED STEP MUST NOT ERASE WHAT IS ALREADY ON SCREEN. React Query keeps
        `data` when a further page fails and merely sets `error`, so branching on
        the error alone meant one transient failure mid-scroll replaced a loaded
        table with a sentence — and left no way back except a reload. The
        blocking message is now only for the case where there is nothing to show;
        otherwise the table stays and the failure is reported beside it.
      */}
      {cards.error && cards.rows.length === 0 ? (
        // The blocking (first-load) error leaves NOTHING on screen — unlike the
        // next-page error below, where the previously-loaded rows still render
        // — so this branch needs its own Retry rather than forcing a full page
        // reload to recover.
        <div className="flex flex-col items-center gap-3 py-8 text-center text-sm text-destructive">
          <p>
            {cards.error instanceof Error ? cards.error.message : 'Failed to load credit cards'}
          </p>
          <Button variant="outline" size="sm" onClick={() => void cards.retry()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          {cards.error ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <span>
                {cards.error instanceof Error
                  ? cards.error.message
                  : 'Could not load more credit cards'}
              </span>
              <Button variant="outline" size="sm" onClick={() => void cards.retry()}>
                Retry
              </Button>
            </div>
          ) : null}
          <DataTable
            title="Credit Cards"
            // The prototype puts the count beside the heading; `subtitle` is
            // where DataTable carries it once there is no pager to host it.
            subtitle={`${cards.totalCount.toLocaleString('en-US')} ${
              cards.totalCount === 1 ? 'card' : 'cards'
            }`}
            itemName="cards"
            columns={columns}
            data={rows}
            rowKey={(row) => row.id}
            // `flex-1 min-h-0` rather than a pixel height: the table takes
            // whatever the column has left after the title and the filters, so it
            // fits every window without a magic number to keep in sync with the
            // chrome above it. `min-h-0` is the part that makes it work — without
            // it a flex child refuses to shrink below its content and the table
            // pushes the page taller instead of scrolling.
            //
            // THE TWO `h-full` VARIANTS ARE WHAT MAKE THE TABLE FILL THE CARD.
            // DataTable nests three wrappers between its root and the table, and
            // only the outermost is a flex child: the `.overflow-x-auto` div and
            // the `Table` primitive's own `relative w-full overflow-auto` div
            // both size to their content. Left alone, a three-row table draws a
            // horizontal scrollbar immediately under the last row with dead card
            // below it.
            //
            // Stretching the inner one also fixes the header, because that div —
            // not the outer wrapper — is the element that actually scrolls, and
            // `position: sticky` resolves against the nearest scrolling ancestor.
            // It has to be pinned from here at all because saas-ui's own
            // `headerClassName` is DEAD (grep it: the only hit is its type
            // declaration) and `stickyHeader` only creates the scroll container
            // its name implies it pins something to.
            //
            // Scrolling inside this container is what loads more: the sentinel
            // row lives in it, so "the end of the table" is the trigger, not
            // "the end of the page".
            className="min-h-0 flex-1 [&_.overflow-x-auto]:h-full [&_.overflow-x-auto>div]:h-full [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-card"
            stickyHeader
            sorting={{
              // SERVER mode: the table holds only what has been scrolled into
              // view, so sorting it in memory would order that slice and not the
              // set. Re-sorting also restarts the accumulation — see the hook.
              mode: 'server',
              sortState,
              onSortChange: setSortState,
            }}
            renderFullWidthRow={(row) => {
              if (!isSentinelRow(row)) return null
              const isEnd = row.edge === 'end'
              return (
                <div
                  ref={isEnd ? endRef : startRef}
                  className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground"
                >
                  {cards.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {cards.isFetching
                    ? 'Loading more cards…'
                    : isEnd
                      ? 'Scroll for more cards'
                      : 'Scroll up for earlier cards'}
                </div>
              )
            }}
            emptyState={
              <p className="text-sm text-muted-foreground">
                {cards.isLoading
                  ? 'Loading credit cards…'
                  : dueRangeInverted
                    ? 'Due to is earlier than Due from — no date can fall in that range.'
                    : 'No credit cards match the filters.'}
              </p>
            }
          />
        </>
      )}
    </div>
  )
}

/**
 * A figure the bank did not publish renders as an em dash, never as $0.00.
 *
 * The vendor is explicit that an absent field means the bank did not report it,
 * and a credit limit of zero reads as a card with no room left — a different
 * fact, and one an AP user would act on.
 *
 * @param value - the amount, or null when unpublished
 */
function money(value: number | null): string {
  return value === null ? '—' : formatCurrency(value)
}
